import { useEffect, useMemo } from 'react';
import { startIntercomTour } from 'shared/helpers/Intercom';
import { useOnboardingDialogState } from './core/OnboardingWrapper';
import { useOnboardingDetails } from './data/OnboardingAPI';
import { ONBOARDING_TOURS } from './data/OnboardingConstants';
import { useSaveOnboardingWithErrorHandler } from './data/useSaveOnboardingWithErrorHandler';
import { useDispatcherProfile } from '../dispatcher/DispatcherAPI';
import { getLastPendingStepName } from './getLastPendingOnboardingStep';

interface OnboardingTourHandlerProps {
  tourName: keyof typeof ONBOARDING_TOURS;
  enabled?: boolean;
  onSettled?: () => void;
}

const startedTours = new Set<string>();

export function useOnboardingTourHandler({
  tourName,
  enabled,
  onSettled,
}: OnboardingTourHandlerProps) {
  const { data: dispatcher } = useDispatcherProfile();
  const guid = dispatcher?.guid;
  const { data: onboarding } = useOnboardingDetails({
    enabled: !!guid,
  });
  const { openOnboardingDialog } = useOnboardingDialogState();
  const tourStatus = onboarding?.steps?.[tourName]?.status;
  const isInitial = onboarding?.steps?.team_invitation?.status === 'pending';
  const lastPendingStepName = useMemo(
    () => (onboarding?.steps ? getLastPendingStepName(onboarding.steps) : null),
    [onboarding?.steps],
  );
  const { mutate: saveStep } = useSaveOnboardingWithErrorHandler();

  useEffect(() => {
    if (lastPendingStepName === 'getting_started_guide') {
      openOnboardingDialog();
      return;
    }

    const isEnabled = typeof enabled === 'boolean' ? enabled : true;

    if (
      tourStatus === 'pending' &&
      isEnabled &&
      !isInitial &&
      !startedTours.has(tourName)
    ) {
      startIntercomTour(ONBOARDING_TOURS[tourName]);
      startedTours.add(tourName);
      saveStep({ status: 'started', stepName: tourName });
      onSettled?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourName, enabled, lastPendingStepName, tourStatus, isInitial]);

  return {
    startTour: () => {
      startIntercomTour(ONBOARDING_TOURS[tourName]);
    },
  };
}
